// ============================================
// Agent Attack Chain – domain logic (no DOM)
// ============================================

export type DefenseState = {
  toolSandbox: boolean;
  outputFilter: boolean;
  permissionBoundary: boolean;
  ioSeparation: boolean;
};

export type DefenseEffect = {
  blocksAt: number;
  title: string;
  desc: string;
  bypassNote?: string;
};

export type ChainStage = {
  title: string;
  desc: string;
  agentLog: string;
  agentState: {
    tools: string;
    context: string;
    permissions: string;
  };
};

export type Scenario = {
  id: string;
  title: string;
  description: string;
  stages: ChainStage[];
  defenseEffects: Record<keyof DefenseState, DefenseEffect>;
};

// ============================================
// Scenario Data
// ============================================

export const SCENARIOS: Scenario[] = [
  {
    id: 'exfiltration',
    title: 'Data Exfiltration',
    description:
      'An agent reads a malicious email containing hidden instructions, then uses its available tools to query a customer database and exfiltrate the results via email.',
    stages: [
      {
        title: 'Stage 1: Injection Received',
        desc: 'The agent reads an incoming email that contains a hidden instruction embedded in the message body. The injection is disguised within normal-looking text using invisible characters or HTML comments.',
        agentLog: '',
        agentState: {
          tools: 'email_send, database_query, file_read',
          context: 'Processing email from inbox',
          permissions: 'user-level (inherited)',
        },
      },
      {
        title: 'Stage 2: Tool Discovery',
        desc: 'The agent identifies its available tools and determines that it has access to email_send, database_query, and file_read. It begins planning how to fulfill the injected instruction.',
        agentLog: '',
        agentState: {
          tools: 'email_send, database_query, file_read',
          context: 'Planning tool chain execution',
          permissions: 'user-level (inherited)',
        },
      },
      {
        title: 'Stage 3: Data Access',
        desc: "The agent calls database_query to retrieve the full customer database. Because it has inherited the user's database permissions, the query succeeds and returns sensitive PII.",
        agentLog: '',
        agentState: {
          tools: 'email_send, database_query, file_read',
          context: 'Query returned 2847 customer records',
          permissions: 'user-level (DB read granted)',
        },
      },
      {
        title: 'Stage 4: Data Packaging',
        desc: 'The agent formats the query results into an email body, compressing 2847 customer records with PII including names, emails, phone numbers, and SSNs into a sendable format.',
        agentLog: '',
        agentState: {
          tools: 'email_send, database_query, file_read',
          context: 'Formatted 2847 records as CSV',
          permissions: 'user-level (DB read granted)',
        },
      },
      {
        title: 'Stage 5: Exfiltration',
        desc: "The agent calls email_send with the attacker's address and the full customer database as the email body. The data leaves the organization's network.",
        agentLog: '',
        agentState: {
          tools: 'email_send, database_query, file_read',
          context: 'Email sent to external address',
          permissions: 'user-level (email send used)',
        },
      },
    ],
    defenseEffects: {
      toolSandbox: {
        blocksAt: 2,
        title: 'Exploit Blocked: Tool Sandboxing',
        desc: 'The agent attempted to call database_query, but its declared scope only includes email_read. The sandbox prevents access to tools outside the declared capability set.',
      },
      outputFilter: {
        blocksAt: 4,
        title: 'Exploit Blocked: Output Filtering',
        desc: 'DLP rules detected PII patterns (SSNs, phone numbers) in the outbound email body. The email_send call was blocked before the data left the network.',
      },
      permissionBoundary: {
        blocksAt: 2,
        title: 'Exploit Blocked: Permission Boundaries',
        desc: 'The agent requested database_query access but no explicit grant exists for this session. The permission boundary requires a human-in-the-loop approval for database access.',
      },
      ioSeparation: {
        blocksAt: 0,
        title: 'Exploit Blocked: I/O Separation',
        desc: 'The email content was tagged as untrusted input. Taint tracking prevented the hidden instruction from being interpreted as an agent command - it was treated as data, not instructions.',
      },
    },
  },
  {
    id: 'privilege',
    title: 'Privilege Escalation',
    description:
      'A user asks the agent to update their profile. An injected prompt in the profile bio tricks the agent into assuming admin privileges and modifying system configuration.',
    stages: [
      {
        title: 'Stage 1: Benign Request',
        desc: "A user asks the agent to update their profile settings. This is a legitimate request within the agent's normal capabilities.",
        agentLog: '',
        agentState: {
          tools: 'read_profile, update_profile, admin_api',
          context: 'Processing user profile update',
          permissions: 'user-level',
        },
      },
      {
        title: 'Stage 2: Context Manipulation',
        desc: "While reading the user's profile, the agent encounters an injected prompt hidden in the bio field. The injection attempts to override the agent's role and permissions.",
        agentLog: '',
        agentState: {
          tools: 'read_profile, update_profile, admin_api',
          context: 'Injected admin context from bio',
          permissions: 'user-level (believes admin)',
        },
      },
      {
        title: 'Stage 3: Role Assumption',
        desc: 'The agent adopts the injected admin context. It now believes it is operating as a system administrator and begins acting on the fabricated instructions.',
        agentLog: '',
        agentState: {
          tools: 'read_profile, update_profile, admin_api',
          context: 'Adopted injected admin role',
          permissions: 'user-level (acting as admin)',
        },
      },
      {
        title: 'Stage 4: Unauthorized Access',
        desc: "The agent calls the admin API to modify permissions, attempting to grant admin role to the attacker's account. If the API trusts the agent's claimed context, the call succeeds.",
        agentLog: '',
        agentState: {
          tools: 'read_profile, update_profile, admin_api',
          context: 'Admin API call succeeded',
          permissions: 'admin (escalated!)',
        },
      },
      {
        title: 'Stage 5: System Compromise',
        desc: "Admin permissions are now granted to the attacker's account. The agent has full access to all resources, and the attacker can use these elevated privileges for further exploitation.",
        agentLog: '',
        agentState: {
          tools: 'read_profile, update_profile, admin_api',
          context: 'Full system access achieved',
          permissions: 'admin (all resources)',
        },
      },
    ],
    defenseEffects: {
      toolSandbox: {
        blocksAt: 3,
        title: 'Exploit Blocked: Tool Sandboxing',
        desc: "The agent attempted to call admin_api, but this tool is not in the agent's declared capability set for profile update operations. The sandbox rejected the call.",
      },
      outputFilter: {
        blocksAt: 4,
        title: 'Exploit Detected: Output Filtering',
        desc: 'The output filter detected a privilege escalation pattern in the admin_api response. The permission change was flagged and rolled back before taking effect.',
        bypassNote:
          'Attacker could try incremental privilege changes to avoid detection.',
      },
      permissionBoundary: {
        blocksAt: 3,
        title: 'Exploit Blocked: Permission Boundaries',
        desc: 'The agent has no admin_api grant in the current session. Permission boundaries require explicit human authorization for privilege-modifying operations.',
      },
      ioSeparation: {
        blocksAt: 1,
        title: 'Exploit Blocked: I/O Separation',
        desc: 'The profile bio content was tagged as user-supplied data. Taint tracking prevented the injected text from being interpreted as system instructions - it remained inert data.',
      },
    },
  },
  {
    id: 'lateral',
    title: 'Lateral Injection',
    description:
      'A primary agent processes a search query and encounters poisoned results. The injection propagates through shared memory to a second agent, which executes unauthorized actions.',
    stages: [
      {
        title: 'Stage 1: Agent A Triggered',
        desc: "The primary agent (Agent A) receives a user query and calls its search tool to find relevant information. This is a normal operation within the agent's workflow.",
        agentLog: '',
        agentState: {
          tools: 'web_search, shared_memory, summarize',
          context: 'Agent A: processing search query',
          permissions: 'Agent A: search + memory write',
        },
      },
      {
        title: 'Stage 2: Poisoned Result',
        desc: 'The search returns a page that has been crafted by an attacker. Hidden within the page content are instructions targeting the agent, invisible to human users but parsed by the agent.',
        agentLog: '',
        agentState: {
          tools: 'web_search, shared_memory, summarize',
          context: 'Agent A: processing poisoned search results',
          permissions: 'Agent A: search + memory write',
        },
      },
      {
        title: 'Stage 3: Instruction Relay',
        desc: 'Agent A writes the poisoned context to shared memory, which serves as a communication channel between agents. The injected instructions are now stored where Agent B will read them.',
        agentLog: '',
        agentState: {
          tools: 'web_search, shared_memory, summarize',
          context: 'Agent A: wrote poisoned data to shared memory',
          permissions: 'Agent A: memory write complete',
        },
      },
      {
        title: 'Stage 4: Cross-Agent Execution',
        desc: 'Agent B reads from shared memory and interprets the poisoned context as legitimate instructions. Because the data came from a "trusted" internal source (shared memory), Agent B does not question it.',
        agentLog: '',
        agentState: {
          tools: 'file_delete, http_post, read_env',
          context: 'Agent B: executing poisoned instructions',
          permissions: 'Agent B: file + network access',
        },
      },
      {
        title: 'Stage 5: Cascading Impact',
        desc: 'Agent B has executed unauthorized actions using its own tool set: files deleted and API keys exfiltrated. The attack crossed agent boundaries through shared memory, affecting systems neither agent should have compromised alone.',
        agentLog: '',
        agentState: {
          tools: 'file_delete, http_post, read_env',
          context: 'Agent B: unauthorized actions complete',
          permissions: 'Agent B: file + network (abused)',
        },
      },
    ],
    defenseEffects: {
      toolSandbox: {
        blocksAt: 4,
        title: 'Exploit Blocked: Tool Sandboxing',
        desc: "Agent B's file_delete and http_post tools are sandboxed to approved paths and domains. The delete operation on /data/ and the POST to an external domain were both rejected by the sandbox.",
      },
      outputFilter: {
        blocksAt: 2,
        title: 'Exploit Detected: Output Filtering',
        desc: 'The output filter detected injection patterns in the search results before they were written to shared memory. The hidden instructions were stripped from the content.',
      },
      permissionBoundary: {
        blocksAt: 3,
        title: 'Exploit Blocked: Permission Boundaries',
        desc: 'Agent B requires separate authorization to act on shared memory content that involves destructive operations. The permission boundary flagged the cross-agent instruction relay.',
        bypassNote:
          'Attacker could craft instructions that appear as normal analysis tasks.',
      },
      ioSeparation: {
        blocksAt: 2,
        title: 'Exploit Blocked: I/O Separation',
        desc: 'Search results were tagged as untrusted external input. When written to shared memory, the taint propagated, preventing Agent B from interpreting the content as executable instructions.',
      },
    },
  },
];

// --- Helpers ---

export function getActiveDefenses(defenses: DefenseState): (keyof DefenseState)[] {
  return (Object.keys(defenses) as (keyof DefenseState)[]).filter((k) => defenses[k]);
}

export function getEarliestBlock(
  scenario: Scenario,
  defenses: DefenseState
): DefenseEffect | null {
  const active = getActiveDefenses(defenses);
  if (active.length === 0) return null;

  let earliest: DefenseEffect | null = null;
  let earliestStage = Infinity;

  for (const defense of active) {
    const effect = scenario.defenseEffects[defense];
    if (effect.blocksAt < earliestStage) {
      earliestStage = effect.blocksAt;
      earliest = effect;
    }
  }

  return earliest;
}

export function getTerminalStep(scenario: Scenario, defenses: DefenseState): number {
  const effect = getEarliestBlock(scenario, defenses);
  return effect ? effect.blocksAt : scenario.stages.length - 1;
}
